// Settings › Integrations › Foundation › Sync — accounting sends approved POs / Change Orders to Foundation from here.
// Queue = ready to send + failed (with the error). Each send = dry run (rolled back) → real insert; "Send all ready" runs them one after another.
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { PanelHeader, PanelCount } from "@/components/settings/SettingsUI";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { foundationSyncSend, getFoundationSyncDocs } from "@/lib/api";
import { FoundationChangedRow, FoundationSyncRow, FoundationStagedRow, rowId } from "./FoundationSyncRow";

const describe = (res) => {
  const st = res?.state || {};
  if (res?.sent || st.status === "pushed" || st.status === "verified") return { ok: true, message: st.message || "Inserted into Foundation" };
  const dr = res?.dry_run || {};
  if (dr.blocked) return { ok: false, message: `Blocked — ${dr.error}` };
  if (dr.nothing_sent || st.error_kind === "unreachable") return { ok: false, message: "Could not reach Foundation — nothing was sent" };
  return { ok: false, message: st.message || dr.error || "Rejected — see the error on the row" };
};

export default function FoundationSyncQueue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [run, setRun] = useState(null);
  const [showStaged, setShowStaged] = useState(false);
  const [showImported, setShowImported] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await getFoundationSyncDocs(); if (alive.current) setData(d); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the Foundation sync queue"); }
    finally { if (alive.current) setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const sendOne = async (row) => {
    setBusy((b) => ({ ...b, [row.key]: true }));
    try {
      const { data: res } = await foundationSyncSend(row.wo_id, row.kind, row.doc_id);
      return describe(res);
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Send failed" };
    } finally { setBusy((b) => ({ ...b, [row.key]: false })); }
  };

  const handleSend = async (row) => {
    const r = await sendOne(row);
    toast[r.ok ? "success" : "error"](`${row.number}: ${r.message}`);
    reload();
  };

  const ready = (data?.rows || []).filter((r) => r.sync_status === "ready");
  const failed = (data?.rows || []).filter((r) => r.sync_status === "failed");
  const changed = data?.changed || [];
  const imported = data?.imported || [];

  const sendAll = async () => {
    setConfirm(false);
    const list = [...ready];
    setRun({ total: list.length, done: 0, current: null, results: [], finished: false });
    for (const row of list) {
      if (!alive.current) return;
      setRun((r) => ({ ...r, current: row }));
      const res = await sendOne(row);
      setRun((r) => ({ ...r, done: r.done + 1, results: [...r.results, { row, ...res }] }));
    }
    setRun((r) => ({ ...r, current: null, finished: true }));
    reload();
  };

  const sending = !!run && !run.finished;
  const bridgeDown = data && !data.bridge?.up;
  const bridgeOld = data && data.bridge?.up && !data.bridge?.payload_only;

  return (
    <div data-testid="foundation-sync-queue">
      <PanelHeader
        icon={Send} iconClass="text-emerald-600" title="Send to Foundation"
        hint={`Purchase Orders · Change Orders${data?.database ? ` · ${data.database}` : ""}`}
        testId="foundation-sync-header"
        actions={<>
          {ready.length > 0 && <PanelCount tone="accent" testId="fsq-ready-count" value={`${ready.length} ready`} />}
          {failed.length > 0 && <PanelCount tone="danger" testId="fsq-failed-count" value={`${failed.length} failed`} />}
          {changed.length > 0 && <PanelCount tone="warn" testId="fsq-changed-count" value={`${changed.length} changed since sent`} />}
        </>}
      />
      <div className="px-5 py-4 space-y-3" data-testid="foundation-sync-body">
        {bridgeDown && (
          <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-center gap-1.5" data-testid="fsq-bridge-down">
            <XCircle className="w-3.5 h-3.5 shrink-0" /> Could not reach Foundation right now — sending is paused. Documents stay here until the bridge is back.
          </div>
        )}
        {bridgeOld && (
          <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 flex items-center gap-1.5" data-testid="fsq-bridge-old">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Bridge {data.bridge.version} still appends audit columns — real inserts are blocked until IT updates it to 2.6.1 (Bridge Ops › Update bridge).
          </div>
        )}

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-slate-500 dark:text-slate-300">
            Each send runs a dry run first (SQL Server rolls it back), then inserts the same rows for real. Accounting then imports in Foundation's
            PO Sync / J/C Change Orders Sync screen. Rejected documents stay here with the exact error.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={reload} disabled={loading || sending} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="fsq-refresh">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button type="button" onClick={() => setConfirm(true)} disabled={!ready.length || sending || bridgeDown || bridgeOld}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="fsq-send-all">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send all ready{ready.length ? ` (${ready.length})` : ""}
            </button>
          </div>
        </div>

        {run && <RunProgress run={run} onDismiss={() => setRun(null)} />}

        {data && data.rows.length === 0 && (
          <div className="text-xs text-slate-400 py-6 text-center" data-testid="fsq-empty">Nothing waiting — every approved PO and Change Order has been sent.</div>
        )}
        {!data && <div className="text-xs text-slate-400 py-6 text-center" data-testid="fsq-loading">Loading…</div>}

        <div className="space-y-2">
          {(data?.rows || []).map((row) => (
            <FoundationSyncRow key={row.key} row={row} busy={!!busy[row.key]} disabled={sending || bridgeDown || bridgeOld}
              current={run?.current?.key === row.key} onSend={() => handleSend(row)} onChanged={reload} />
          ))}
        </div>

        {changed.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-amber-200" data-testid="fsq-changed-list">
            <div className="text-[11px] text-amber-900 dark:text-amber-200">
              <span className="font-semibold">Changed since sent</span> — these POs were edited in ServiceOps after Foundation received them. Key the change into Foundation's PO
              by hand, then click <b>Updated in Foundation</b> to clear the flag.
            </div>
            {changed.map((row) => <FoundationChangedRow key={row.key} row={row} onChanged={reload} />)}
          </div>
        )}

        {data && (
          <div className="text-[11px] text-slate-500 dark:text-slate-300 flex items-center gap-2 flex-wrap pt-2 border-t border-slate-200 dark:border-slate-700" data-testid="fsq-footer">
            <span data-testid="fsq-staged-count">{data.counts.pushed} staged in Foundation awaiting import</span> · <span data-testid="fsq-imported-count">{data.counts.verified} imported</span>
            {data.staged.length > 0 && (
              <button type="button" onClick={() => setShowStaged((v) => !v)} className="text-blue-600 hover:underline" data-testid="fsq-toggle-staged">
                {showStaged ? "Hide staged" : `Show staged (${data.staged.length})`}
              </button>
            )}
            {imported.length > 0 && (
              <button type="button" onClick={() => setShowImported((v) => !v)} className="text-blue-600 hover:underline" data-testid="fsq-toggle-imported">
                {showImported ? "Hide imported" : `Show imported (${imported.length})`}
              </button>
            )}
            {data.bridge?.up && !data.bridge?.staging_clear && (
              <span className="text-amber-800" title="Unlock to revise needs the bridge's staging-clear capability" data-testid="fsq-no-staging-clear">
                · Bridge {data.bridge.version}: Unlock to revise needs 2.6.2
              </span>
            )}
          </div>
        )}
        {showStaged && data?.staged?.length > 0 && (
          <div className="space-y-2" data-testid="fsq-staged-list">
            {data.staged.map((row) => <FoundationStagedRow key={row.key} row={row} onChanged={reload} stagingClear={!!data.bridge?.staging_clear} />)}
          </div>
        )}
        {showImported && imported.length > 0 && (
          <div className="space-y-2" data-testid="fsq-imported-list">
            {imported.map((row) => <FoundationStagedRow key={row.key} row={row} onChanged={reload} stagingClear={!!data.bridge?.staging_clear} />)}
          </div>
        )}
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="fsq-send-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">Send {ready.length} document{ready.length === 1 ? "" : "s"} to Foundation?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs space-y-1">
              <span className="block">Database: <span className="font-mono">{data?.database}</span>. Each document gets a dry run first; if SQL Server accepts it, the same rows are inserted for real — one after another, no further clicks.</span>
              <span className="block">Anything rejected stays in this queue with its error. Already-staged documents are never written twice.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="text-[11px] font-mono max-h-40 overflow-y-auto space-y-0.5 text-slate-700 dark:text-slate-200" data-testid="fsq-send-all-list">
            {ready.map((r) => <li key={r.key}>{r.kind === "purchase_order" ? "PO" : "CO"} {r.number} · WO {r.wo_number}</li>)}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fsq-send-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendAll} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fsq-send-all-confirm">Send {ready.length} now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RunProgress({ run, onDismiss }) {
  const sent = run.results.filter((r) => r.ok).length;
  const failed = run.results.length - sent;
  return (
    <div className={`rounded-md border p-2.5 text-xs ${run.finished ? (failed ? "border-rose-200 bg-rose-50 text-rose-900" : "border-emerald-200 bg-emerald-50 text-emerald-900") : "border-sky-200 bg-sky-50 text-sky-900"}`} data-testid="fsq-run">
      <div className="font-semibold flex items-center gap-1.5" data-testid="fsq-run-headline">
        {run.finished ? (failed ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />) : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {run.finished
          ? `Done — ${sent} sent, ${failed} failed of ${run.total}`
          : `Sending ${Math.min(run.done + 1, run.total)} of ${run.total}${run.current ? ` — ${run.current.kind === "purchase_order" ? "PO" : "CO"} ${run.current.number}` : ""}…`}
        {run.finished && <button type="button" onClick={onDismiss} className="ml-auto text-[11px] underline" data-testid="fsq-run-dismiss">Dismiss</button>}
      </div>
      {run.results.length > 0 && (
        <ul className="mt-1.5 space-y-0.5" data-testid="fsq-run-results">
          {run.results.map((r) => (
            <li key={r.row.key} className="flex items-start gap-1.5" data-testid={`fsq-run-result-${rowId(r.row)}`}>
              {r.ok ? <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-600 shrink-0" /> : <XCircle className="w-3 h-3 mt-0.5 text-rose-600 shrink-0" />}
              <span className="font-mono">{r.row.number}</span><span className="opacity-80">— {r.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
